import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AxiosError } from 'axios';

import { useForm } from 'react-hook-form';

import { useBooleanState } from '@vyce/core/src/hooks';
import {
  deleteAllWorkerShiftsRequest,
  getLocationUsersRequest,
  getCompaniesLocationWorkersSharedData,
  deleteWorkersFromLocationRequest,
} from '@vyce/core/src/api/time';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useWidgetErrorHandler } from '@vyce/core/src/hooks/useWidgetErrorHandler';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { useInfiniteScroll } from '@vyce/core/src/hooks/useInfiniteScroll';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { useMainDashboardFilter } from '@vyce/core/src/hooks/useMainDashboardFilter';

import { ExtendedLocationUserDTO, Props } from '../types';
import { defaultLocationOption, defaultSelectedLocationOption, defaultStatusOption } from '../config';
import { generateExcelFileLink } from '../../../../utils';
import { LocationUsersBarchart } from '../../../../api/types';
import useChatRequests from '../../../../hooks/useChatRequests';

export const useWidgetData = ({ selectedCompanyId, mainDashboardFilters, chatUser, onForbidden }: Props) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  // Loading the widget: a 403 hides it instead of raising a toast.
  const handleWidgetError = useWidgetErrorHandler(onForbidden);
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const [isConfirmDeleteDialogOpen, openConfirmDeleteDialog, closeConfirmDeleteDialog] =
    useBooleanState(false);
  const [downloadButtonLoading, setDownloadButtonLoadingTrue, setDownloadButtonLoadingFalse] =
    useBooleanState(false);
  const [isShowSearchField, setShowSearchFieldTrue] = useBooleanState(false);
  const { removeUsersFromChat } = useChatRequests(chatUser);

  const [selectedStatusId, setSelectedStatusId] = useState(defaultStatusOption.value);
  const [uniqueUsersCount, setUniqueUsersCount] = useState(0);
  const [barchartData, setBarchartData] = useState<
    (LocationUsersBarchart & { onBarchartClick: (status: string) => void })[] | null
  >(null);

  const [isSmallView, setSmallView] = useState(false);
  const [candidateToRemove, setCandidateToRemove] = useState<{
    user: ExtendedLocationUserDTO;
    location: string;
  } | null>(null);

  const { total, substring, sortModel, setTotal, handleSearchChange, handleSortModelChange } = useTable({});
  const {
    isLoadingList,
    data,
    setNewData,
    setInitialLoadingStatusesFalse,
    setInitialLoadingStatusesTrue,
    resetLoadingList,
  } = useInfiniteScroll<ExtendedLocationUserDTO>([]);
  const { isMobile } = useContext(DeviceContext);

  const { selectedLocationOptions, unselectedLocationOptions, getLocationsForRequest } =
    useMainDashboardFilter({
      mainDashboardFilters,
      locationOption: defaultLocationOption,
      selectedLocationOption: defaultSelectedLocationOption,
    });
  const methods = useForm<{ location: string }>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      location: defaultLocationOption.value,
    },
  });

  const { watch, setValue } = methods;
  const location = watch('location');

  const locationIds = useMemo(() => getLocationsForRequest(location), [location, getLocationsForRequest]);

  const removeWorkerFromOneLocation = async (user: ExtendedLocationUserDTO) => {
    if (!selectedCompanyId || !user.site_id || !user.user_id) {
      return;
    }

    setLoadingTrue();
    try {
      await deleteWorkersFromLocationRequest(selectedCompanyId, user.site_id, [user.user_id]);
      showNotification({ message: 'User has been removed', options: { variant: 'success' } });
      removeUsersFromChat({
        companyId: selectedCompanyId,
        entityId: user.site_id,
        userIds: [user.user_id],
      });
      getLocationUsers();
      closeConfirmDialog();
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoadingFalse();
    }
  };

  const removeWorkerFromAllLocations = async (user: ExtendedLocationUserDTO) => {
    if (!selectedCompanyId || !user.user_id) {
      return;
    }
    setLoadingTrue();
    try {
      await deleteAllWorkerShiftsRequest(selectedCompanyId, user.user_id);
      showNotification({ message: 'User has been removed', options: { variant: 'success' } });
      getLocationUsers();
      closeConfirmDialog();
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoadingFalse();
    }
  };

  const handleRemoveUser = useCallback((user: ExtendedLocationUserDTO, location: string) => {
    setCandidateToRemove({ user, location });
    openConfirmDeleteDialog();
  }, []);

  const handleChangeStatusIdViaBarchart = (status: string) => {
    if (status === selectedStatusId) {
      setSelectedStatusId(defaultStatusOption.value);
      return;
    }
    setSelectedStatusId(status);
  };

  const removeUserFromLocation = () => {
    if (candidateToRemove?.location === 'all') {
      removeWorkerFromAllLocations(candidateToRemove.user);
    } else if (candidateToRemove?.user) {
      removeWorkerFromOneLocation(candidateToRemove.user);
    }
  };

  const closeConfirmDialog = () => {
    setCandidateToRemove(null);
    closeConfirmDeleteDialog();
  };

  const getLocationUsers = async (offset: number = 0, signal?: AbortSignal) => {
    if (!selectedCompanyId) {
      return;
    }

    const itemStatusMap: Record<number, boolean> = setInitialLoadingStatusesFalse(offset, GRID_PAGE_SIZE);

    setLoadingTrue();
    try {
      const { data } = await getLocationUsersRequest({
        companyId: selectedCompanyId,
        data: {
          limit: GRID_PAGE_SIZE,
          offset,
          site_ids: getLocationsForRequest(location),
          status: selectedStatusId !== 'all' ? selectedStatusId : undefined,
          substring,
        },
        signal,
      });

      const transformedData = data.items.map(item => ({
        ...item,
        locationId: `${item.user_id}_${item.location}`,
      }));
      setTotal(data.count);
      setUniqueUsersCount(data.unique_users_count);
      setNewData({ newData: transformedData, offset });
      if (data.users_statuses) {
        setBarchartData([{ ...data.users_statuses, onBarchartClick: handleChangeStatusIdViaBarchart }]);
      }
      setInitialLoadingStatusesTrue({ offset, pageSize: GRID_PAGE_SIZE, itemStatusMap });
      setLoadingFalse();
    } catch (e) {
      const err = e as AxiosError;
      if (err.name !== 'CanceledError') {
        handleWidgetError(err);
        setLoadingFalse();
      }
    }
  };

  const getSharedFile = useCallback(async () => {
    if (!selectedCompanyId) {
      return;
    }

    setDownloadButtonLoadingTrue();
    try {
      const res = await getCompaniesLocationWorkersSharedData({
        companyId: selectedCompanyId,
        data: {
          limit: GRID_PAGE_SIZE,
          offset: 0,
          site_ids: locationIds,
          status: selectedStatusId !== 'all' ? selectedStatusId : undefined,
          substring,
        },
      });

      generateExcelFileLink(res);
    } catch (e) {
      handleServerError(e);
    } finally {
      setDownloadButtonLoadingFalse();
    }
  }, [selectedCompanyId, selectedStatusId, substring, locationIds]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    getLocationUsers(0, signal);
    return () => {
      controller.abort();
    };
  }, [
    selectedCompanyId,
    selectedStatusId,
    location,
    substring,
    selectedLocationOptions,
    unselectedLocationOptions,
  ]);

  useEffect(() => {
    if (selectedLocationOptions.length) {
      setValue('location', selectedLocationOptions[0].value);
    } else {
      setValue('location', unselectedLocationOptions[0].value);
    }
  }, [selectedLocationOptions, unselectedLocationOptions]);

  return {
    locationUsers: data,
    total,
    loading,
    selectedLocationOptions,
    unselectedLocationOptions,
    selectedStatusId,
    methods,
    isConfirmDeleteDialogOpen,
    candidateToRemove,
    isMobile,
    handleSearchChange,
    isLoadingList,
    sortModel,
    isSmallView,
    downloadButtonLoading,
    isShowSearchField,
    uniqueUsersCount,
    barchartData,
    setShowSearchFieldTrue,
    getSharedFile,
    setSmallView,
    handleSortModelChange,
    resetLoadingList,
    getLocationUsers,
    handleRemoveUser,
    setSelectedStatusId,
    closeConfirmDialog,
    removeUserFromLocation,
    location,
  };
};
